import isEmpty from 'lodash/isEmpty';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import {
    IArgumentCategories,
    ICategoryItem,
    IConditionWithCollection,
    IConditionWithValue,
    IPolicyConditionWithArgMetadata,
    ISetCondition,
} from '@tymely/atoms';
import { CopyToClipboardButton, MultiSelect } from '@tymely/components';
import { Nullable } from '@global/types';
import TagsInput from '@tymely/components/TagsInput';
import { Box, Button, Popover, Typography } from '@mui/material';
import { CopyAllIconStyle } from '@tymely/pages/GraphPolicyEditor/components/PolicyGraph/edges/ConditionEditors/CategoricalConditionEditor/styles';

import { BaseConditionEditor, ConditionEditorProps } from '../BaseConditionEditor';

const CategoricalConditionEditor = memo(
    (
        props: ConditionEditorProps<
            IConditionWithCollection | ISetCondition | IConditionWithValue<'equals' | 'not_equals', string>
        > & {
            categories?: Nullable<IArgumentCategories<string> | IArgumentCategories<ICategoryItem>>;
        },
    ) => {
        const categories = props.categories ?? {};
        const predicate = props.condition.predicate;
        const multiple =
            props.argumentMetadata.dtype.startsWith('list[') || (predicate !== 'equals' && predicate !== 'not_equals');

        const options = useMemo(
            () =>
                Object.entries(categories).map(([key, value]) => ({
                    value: key,
                    name: value,
                })),
            [categories],
        );

        const handleChangeValue = useCallback(
            (selectedOptions: Nullable<typeof options>) => {
                if (!selectedOptions) return;
                const collection = Object.keys(categories).filter((cat) =>
                    selectedOptions.find((opt) => opt.value === cat),
                );
                props.setCondition({
                    argument_metadata: props.argumentMetadata,
                    argument_metadata_id: props.argumentMetadata.id,
                    ...props.condition,
                    value: {
                        ...props.condition.value,
                        value: multiple ? collection : collection[0],
                    },
                } as IPolicyConditionWithArgMetadata);
            },
            [categories, props.condition, multiple, predicate, props.argumentMetadata, props.setCondition],
        );

        const selectedOptions = useMemo(
            () =>
                options.filter((opt) => ([] as string[]).concat(props.condition.value.value || []).includes(opt.value)),
            [options, props.condition.value?.value],
        );

        const tags = useMemo(() => {
            const value = props.condition.value.value;
            if (!value) {
                return [];
            }
            if (Array.isArray(value)) {
                return value;
            }
            return [value];
        }, [props.condition.value.value]);

        const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

        const handleClose = () => {
            setAnchorEl(null);
        };

        const open = Boolean(anchorEl);
        useEffect(() => {
            if (open) {
                setTimeout(() => {
                    textFieldRef.current?.focus();
                }, 100);
            }
        }, [open]);

        const textFieldRef = useRef<HTMLInputElement>(null);
        const value = props.condition.value.value;
        const joinedValues = Array.isArray(value) ? value.join(', ') : value;

        return (
            <BaseConditionEditor {...props}>
                {isEmpty(categories) ? (
                    <>
                        <Button
                            onClick={(event) => setAnchorEl(event.currentTarget)}
                            color="secondary"
                            size="small"
                            sx={{
                                borderRadius: '4px',
                                py: 1,
                                color: (theme) => theme.palette.text.secondary,
                                '&:hover': {
                                    backgroundColor: props.disabled
                                        ? 'transparent'
                                        : (theme) => theme.palette.grey[200],
                                },
                            }}
                        >
                            <Typography variant="body1">
                                {joinedValues
                                    ? joinedValues.slice(0, 20) + (joinedValues.length > 20 ? '...' : '')
                                    : 'Select values'}
                            </Typography>
                        </Button>
                        <Popover
                            open={open}
                            anchorEl={anchorEl}
                            onClose={handleClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                        >
                            <Box>
                                <CopyToClipboardButton text={tags.join('\n')} sx={CopyAllIconStyle} />
                                <TagsInput
                                    ref={textFieldRef}
                                    tags={tags}
                                    onSelectTags={(tags) => {
                                        props.setCondition({
                                            argument_metadata: props.argumentMetadata,
                                            argument_metadata_id: props.argumentMetadata.id,
                                            ...props.condition,
                                            value: {
                                                ...props.condition.value,
                                                value: tags,
                                            },
                                        } as IPolicyConditionWithArgMetadata);
                                    }}
                                    placeholder="Type values"
                                    onFocus={() => props.onInputFocusChange?.(true)}
                                    onBlur={() => props.onInputFocusChange?.(false)}
                                    onDone={handleClose}
                                />
                            </Box>
                        </Popover>
                    </>
                ) : (
                    <MultiSelect
                        options={options}
                        value={selectedOptions}
                        onOptsSelect={handleChangeValue}
                        multiple={multiple}
                        disabled={props.disabled}
                        variant="standard"
                        disablePortal={props.disablePortal}
                        sx={{
                            borderRadius: '4px',
                            px: 1,
                            py: 0.5,
                            '&:hover': {
                                backgroundColor: props.disabled ? 'transparent' : (theme) => theme.palette.grey[200],
                            },
                            '&:focus-within': {
                                backgroundColor: props.disabled ? 'transparent' : (theme) => theme.palette.grey[200],
                            },
                        }}
                        hideArrow
                    />
                )}
            </BaseConditionEditor>
        );
    },
);

CategoricalConditionEditor.displayName = 'CategoricalConditionEditor';

export default CategoricalConditionEditor;
